import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { UpgradeProvider } from "@/context/UpgradeContext";
import AppShell from "@/components/app/AppShell";
import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import MagicVerify from "@/pages/MagicVerify";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import DataDeletion from "@/pages/legal/DataDeletion";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Goals from "@/pages/Goals";
import GoalDetail from "@/pages/GoalDetail";
import Coach from "@/pages/Coach";
import Learn from "@/pages/Learn";
import Reviews from "@/pages/Reviews";
import Settings from "@/pages/Settings";
import Admin from "@/pages/Admin";
import Pricing from "@/pages/Pricing";
import Family from "@/pages/Family";
import Student from "@/pages/Student";
import Vision from "@/pages/Vision";
import Calendar from "@/pages/Calendar";
import Community from "@/pages/Community";
import LifeScore from "@/pages/LifeScore";
import VoiceCoach from "@/pages/VoiceCoach";
import Journal from "@/pages/Journal";
import Sports from "@/pages/Sports";

function FullLoader() {
  return (
    <div className="relative z-10 flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-gold" />
    </div>
  );
}

function RequireAuth({ children, requireOnboarded = true }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (requireOnboarded && !user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

function AppLayout() {
  return (
    <RequireAuth>
      <AppShell><Outlet /></AppShell>
    </RequireAuth>
  );
}

function App() {
  return (
    <div className="App relative bg-background min-h-screen text-foreground antialiased">
      <div className="grid-bg" aria-hidden="true" />
      <div className="grain-overlay" aria-hidden="true" />
      <Toaster position="top-center" theme="dark"
        toastOptions={{ style: { background: "#0C0C0E", border: "1px solid rgba(168,85,247,0.4)", color: "#F4F2EE" } }} />
      <BrowserRouter>
        <AuthProvider>
          <UpgradeProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Auth mode="login" />} />
            <Route path="/register" element={<Auth mode="register" />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/magic" element={<MagicVerify />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/data-deletion" element={<DataDeletion />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/onboarding" element={<RequireAuth requireOnboarded={false}><Onboarding /></RequireAuth>} />
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="goals" element={<Goals />} />
              <Route path="goals/:id" element={<GoalDetail />} />
              <Route path="coach" element={<Coach />} />
              <Route path="learn" element={<Learn />} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="vision" element={<Vision />} />
              <Route path="student" element={<Student />} />
              <Route path="family" element={<Family />} />
              <Route path="reviews" element={<Reviews />} />
              <Route path="community" element={<Community />} />
              <Route path="lifescore" element={<LifeScore />} />
              <Route path="voice" element={<VoiceCoach />} />
              <Route path="journal" element={<Journal />} />
              <Route path="sports" element={<Sports />} />
              <Route path="sports/:sessionId" element={<Sports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin" element={<Admin />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </UpgradeProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
